import React, { FormEvent, useState } from 'react';
import { Button, Card, Input } from '@tadagram/ui';
import { useMarketplaceEngine } from '../hooks/useMarketplaceEngine';
import { ListingDraft } from '../domain/marketplaceTypes';

function createActionKey(): string {
  return `listing-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const LISTING_TYPE_OPTIONS = [
  { value: 'seeding', label: 'Seeding Account' },
  { value: 'ai', label: 'AI Account' },
];

const PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'zalo', label: 'Zalo' },
  { value: 'other', label: 'Khác' },
];

const Sell: React.FC = () => {
  const { createListing } = useMarketplaceEngine();
  const [draft, setDraft] = useState<ListingDraft>({ title: '', type: 'seeding', platform: 'facebook', credits: '', banner: '', description: '' });
  const [notice, setNotice] = useState<{ ok: boolean; message: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setNotice(null);
    const result = await createListing(draft, createActionKey());
    setNotice(result);
    setLoading(false);
    if (result.ok) setDraft({ title: '', type: draft.type, platform: draft.type === 'seeding' ? 'facebook' : undefined, credits: '', banner: '', description: '' });
  };

  const set = (field: keyof ListingDraft) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setDraft((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="max-w-2xl mx-auto">
      <Card className="p-7">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900 m-0 mb-1.5">
            Đăng sản phẩm mới
          </h2>
          <p className="text-sm text-slate-500 m-0">
            Sản phẩm ở trạng thái <strong className="text-slate-700">Chờ duyệt</strong> trước khi được công khai — đảm bảo minh bạch giao dịch 2 chiều.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4.5">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-900">Tên sản phẩm *</label>
            <Input value={draft.title} onChange={set('title')}
              placeholder="VD: Facebook Seeding Account 5K Follower" required />
          </div>

          <div className="flex flex-col gap-1.5 mt-4">
            <label className="text-sm font-semibold text-slate-900">Danh mục *</label>
            <select className="w-full p-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 outline-none cursor-pointer" value={draft.type} onChange={(e) => {
              const newType = e.target.value as any;
              setDraft(p => ({ ...p, type: newType, platform: newType === 'seeding' ? 'facebook' : undefined }));
            }}>
              {LISTING_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {draft.type === 'seeding' && (
            <div className="flex flex-col gap-1.5 mt-4">
              <label className="text-sm font-semibold text-slate-900">Nền tảng *</label>
              <select className="w-full p-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 outline-none cursor-pointer" value={draft.platform || 'facebook'} onChange={set('platform')}>
                {PLATFORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1.5 mt-4">
            <label className="text-sm font-semibold text-slate-900">Giá (Tada Credits) *</label>
            <Input type="number" value={draft.credits} onChange={set('credits')}
              placeholder="Tối thiểu 10, tối đa 200,000" min={10} max={200000} required />
          </div>

          <div className="flex flex-col gap-1.5 mt-4">
            <label className="text-sm font-semibold text-slate-900">Banner / Emoji đại diện</label>
            <Input value={draft.banner} onChange={set('banner')}
              placeholder="VD: 🚀 hoặc FB5K" maxLength={6} />
          </div>

          <div className="flex flex-col gap-1.5 mt-4">
            <label className="text-sm font-semibold text-slate-900">Mô tả sản phẩm</label>
            <textarea className="w-full p-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 outline-none resize-y"
              value={draft.description} onChange={set('description')}
              placeholder="Mô tả chi tiết tài khoản, dịch vụ kèm theo…" rows={4} maxLength={500} />
          </div>

          {notice && (
            <div
              className={`p-3 rounded-lg text-sm font-medium mt-4 ${notice.ok ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'}`}
            >
              {notice.message}
            </div>
          )}

          <div className="mt-4">
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Đang đăng…' : 'Đăng sản phẩm'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default Sell;
